import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './FloatingPanel.css';
import api, { AreaStats } from '../../services/api';
import qotdService, { ActiveQotdResponse } from '../../services/qotdService';
import { getColorForPercentage, getFaceIconByPercentage, getCityLogoByPercentage, getNoVotesCityLogo, getNoVotesFaceIcon, NO_VOTES_COLOR } from '../../utils/emotionUtils';
import languageSelectorIcon from '../../assets/icons/language-selector.svg';
import instagramIcon from '../../assets/icons/instagram.svg';
import tiktokIcon from '../../assets/icons/tik-tok.svg';
import blueskyIcon from '../../assets/icons/bluesky.svg';
import appStoreBadge from '../../assets/icons/Download_on_the_App_Store_Badge_US-UK_RGB_wht_092917.svg';
import hwaLogo from '../../assets/ui/HWA.svg';
import nycLogo from '../../assets/ui/NYC.svg';
import qotdLogo from '../../assets/ui/QOTD.svg';
import buttonGood from '../../assets/ui/button-good.svg';
import buttonBad from '../../assets/ui/button-bad.svg';
import { useTranslation } from '../../hooks/useTranslation';

const BOROUGH_ABBREVIATIONS: { [key: string]: string } = {
  Manhattan: 'MN',
  Brooklyn: 'BK',
  Queens: 'QN',
  Bronx: 'BX',
  'Staten Island': 'SI'
};

const BOROUGH_ORDER = ['Brooklyn', 'Queens', 'Manhattan', 'Bronx', 'Staten Island'];
const APP_STORE_URL = 'https://apps.apple.com/us/app/hwa-nyc-how-we-are/id6765759565';

type QotdCardState = 'closed' | 'prompt' | 'results';
type VoteSelection = 'good' | 'bad' | null;

interface FloatingPanelProps {
  onVote?: (isPositive: boolean) => Promise<void>;
  isSubmitting?: boolean;
  location?: { latitude: number; longitude: number };
  hasVoted?: boolean;
  canVote?: boolean;
  nextVoteTime?: string | null;
  isInitializing?: boolean;
  lastVote?: {
    is_positive: boolean;
    neighborhood_name: string;
  } | null;
  isLockedView?: boolean;
  isOutsideNYC?: boolean;
  unlockAnimationActive?: boolean;
  unlockVoteSelection?: VoteSelection;
}

const FloatingPanel: React.FC<FloatingPanelProps> = ({
  onVote,
  isSubmitting = false,
  location,
  hasVoted = false,
  canVote = false,
  nextVoteTime,
  isInitializing = false,
  lastVote = null,
  isLockedView = false,
  isOutsideNYC = false,
  unlockAnimationActive = false,
  unlockVoteSelection = null
}) => {
  const { t, currentLanguage, changeLanguage } = useTranslation();
  const [cityStats, setCityStats] = useState<{ totalVotes: number; positivePercentage: number } | null>(null);
  const [boroughsData, setBoroughsData] = useState<AreaStats[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [qotd, setQotd] = useState<ActiveQotdResponse | null>(null);
  const [qotdLoading, setQotdLoading] = useState(false);
  const [qotdCardState, setQotdCardState] = useState<QotdCardState>('closed');
  const [showMoodPrompt, setShowMoodPrompt] = useState(false);
  const moodTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const allAreasStats = await api.getAllAreasStats();
        const boroughs = allAreasStats.filter(area => area.area_type === 'borough');
        const orderedBoroughs = BOROUGH_ORDER
          .map(name => boroughs.find(b => b.name === name))
          .filter(Boolean) as AreaStats[];
        setBoroughsData(orderedBoroughs);
        const totalVotes = boroughs.reduce((sum, b) => sum + b.total_votes, 0);
        const positiveVotes = boroughs.reduce((sum, b) => sum + b.positive_votes, 0);
        const positivePercentage = totalVotes > 0 ? (positiveVotes / totalVotes) * 100 : 50;
        setCityStats({ totalVotes, positivePercentage });
      } catch (error) {
        console.error('Error al cargar datos del panel:', error);
      }
    };
    loadData();
    const interval = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadQotd = useCallback(async (): Promise<ActiveQotdResponse | null> => {
    try {
      setQotdLoading(true);
      const response = await qotdService.getActive(currentLanguage);
      setQotd(response);
      return response;
    } catch (error) {
      console.error('Error loading QOTD:', error);
      return null;
    } finally {
      setQotdLoading(false);
    }
  }, [currentLanguage]);

  useEffect(() => {
    if (isLockedView || isOutsideNYC) return;
    loadQotd();
  }, [isLockedView, isOutsideNYC, loadQotd]);

  useEffect(() => {
    if (isLockedView || isOutsideNYC) return;
    const interval = window.setInterval(() => {
      loadQotd();
    }, 15000);
    return () => window.clearInterval(interval);
  }, [isLockedView, isOutsideNYC, loadQotd]);

  useEffect(() => {
    if (qotdCardState === 'closed') return;
    if (qotd?.results?.has_voted) {
      setQotdCardState('results');
    }
  }, [qotdCardState, qotd?.results?.has_voted]);

  const submitQotdVote = async (isPositive: boolean) => {
    if (!location) return;
    try {
      setQotdLoading(true);
      await qotdService.vote({
        latitude: location.latitude,
        longitude: location.longitude,
        isPositive
      });
      const fresh = await qotdService.getActive(currentLanguage);
      setQotd(fresh);
      setQotdCardState('results');
    } catch (error) {
      console.error('Error submitting QOTD vote:', error);
    } finally {
      setQotdLoading(false);
    }
  };

  const openQotdCard = async () => {
    setShowMoodPrompt(false);
    const fresh = await loadQotd();
    setQotdCardState(fresh?.results?.has_voted ? 'results' : 'prompt');
  };

  const closeQotdCard = () => {
    setQotdCardState('closed');
  };

  const resetMoodPromptTimer = useCallback(() => {
    if (moodTimeoutRef.current) {
      window.clearTimeout(moodTimeoutRef.current);
      moodTimeoutRef.current = null;
    }
    setShowMoodPrompt(true);
    moodTimeoutRef.current = window.setTimeout(() => {
      setShowMoodPrompt(false);
      moodTimeoutRef.current = null;
    }, 30000);
  }, []);

  useEffect(() => {
    if (isLockedView || isOutsideNYC) return;
    if (!canVote) {
      setShowMoodPrompt(false);
      if (moodTimeoutRef.current) {
        window.clearTimeout(moodTimeoutRef.current);
        moodTimeoutRef.current = null;
      }
      return;
    }
    resetMoodPromptTimer();
  }, [canVote, isLockedView, isOutsideNYC, resetMoodPromptTimer]);

  useEffect(() => {
    const onVisible = () => {
      if (document.hidden) return;
      if (!isLockedView && !isOutsideNYC && canVote) {
        resetMoodPromptTimer();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      if (moodTimeoutRef.current) {
        window.clearTimeout(moodTimeoutRef.current);
        moodTimeoutRef.current = null;
      }
    };
  }, [canVote, isLockedView, isOutsideNYC, resetMoodPromptTimer]);

  const cityLogo = cityStats
    ? (cityStats.totalVotes > 0 ? getCityLogoByPercentage(cityStats.positivePercentage) : getNoVotesCityLogo())
    : null;
  const cityMoodFace = cityStats
    ? (cityStats.totalVotes > 0 ? getFaceIconByPercentage(cityStats.positivePercentage) : getNoVotesFaceIcon())
    : null;
  const qotdHasVoted = Boolean(qotd?.results?.has_voted);

  const lockedVotingCopyRaw = t('voting.title', 'How are you today?');
  const lockedVotingCopy =
    lockedVotingCopyRaw.trim().toLowerCase() === "how's your mood today?"
      ? 'How are you today?'
      : lockedVotingCopyRaw;

  const mobileVotingCopy = isLockedView ? lockedVotingCopy : t('voting.mood_now', 'How are you now?');

  const languageEntries = useMemo(
    () =>
      Object.entries({
        en: 'English',
        es: 'Español',
        zh: '中文',
        ru: 'Русский',
        it: 'Italiano',
        ht: 'Kreyòl',
        ko: '한국어',
        fr: 'Français',
        tl: 'Tagalog',
        pl: 'Polski',
        ar: 'العربية',
        yi: 'ייִדיש',
        hi: 'हिन्दी',
        ur: 'اردو',
        el: 'Ελληνικά',
        he: 'עברית',
        pt: 'Português'
      }),
    []
  );

  const renderMobileVoteButtons = (className = '') => (
    <div className={`mood-vote-buttons ${className}`.trim()}>
      <button
        className={`mood-vote-button mood-vote-button-good ${
          unlockAnimationActive && unlockVoteSelection === 'bad' ? 'is-hidden-unselected' : ''
        } ${
          unlockAnimationActive && unlockVoteSelection === 'good' ? 'is-selected-expand' : ''
        }`}
        onClick={() => onVote?.(true)}
        disabled={isSubmitting || isInitializing}
      >
        <img src={buttonGood} alt={t('voting.good', 'Good')} />
      </button>
      <button
        className={`mood-vote-button mood-vote-button-bad ${
          unlockAnimationActive && unlockVoteSelection === 'good' ? 'is-hidden-unselected' : ''
        } ${
          unlockAnimationActive && unlockVoteSelection === 'bad' ? 'is-selected-expand' : ''
        }`}
        onClick={() => onVote?.(false)}
        disabled={isSubmitting || isInitializing}
      >
        <img src={buttonBad} alt={t('voting.bad', 'Bad')} />
      </button>
    </div>
  );

  const renderAppStoreBadge = (className = '') => (
    <a
      href={APP_STORE_URL}
      className={`app-store-badge-link ${className}`.trim()}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download HWA NYC on the App Store"
    >
      <img src={appStoreBadge} alt="Download on the App Store" />
    </a>
  );

  const mobileHeader = (
    <div className="mobile-top-header">
      <div className="mobile-top-header-main-row">
        <button className="mobile-menu-btn" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M3 12H21M3 6H21M3 18H21" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <div className="mobile-brand-row">
          <img src={hwaLogo} className="mobile-brand-logo-hwa" alt="HWA" />
          {cityMoodFace ? <img src={cityMoodFace} className="mobile-brand-face" alt="City mood" /> : null}
          <img src={nycLogo} className="mobile-brand-logo-nyc" alt="NYC" />
        </div>
        <button className="mobile-menu-btn" onClick={() => setIsLanguageMenuOpen(true)} aria-label="Select language">
          <img src={languageSelectorIcon} alt="Language" />
        </button>
      </div>
      {boroughsData.length > 0 ? (
        <div className="mobile-boroughs-badges">
          {boroughsData.map(borough => {
            const hasVotes = borough.total_votes > 0;
            const positivePercentage = hasVotes ? (borough.positive_votes / borough.total_votes) * 100 : 50;
            const color = hasVotes ? getColorForPercentage(positivePercentage) : NO_VOTES_COLOR;
            const faceIcon = hasVotes ? getFaceIconByPercentage(positivePercentage) : getNoVotesFaceIcon();
            const abbreviation = BOROUGH_ABBREVIATIONS[borough.name] || borough.name;
            return (
              <div key={borough.id} className="mobile-badge" style={{ backgroundColor: color }}>
                <span>{abbreviation}</span>
                {faceIcon ? <img src={faceIcon} alt="" /> : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );

  const qotdPill = !isLockedView ? (
    isOutsideNYC ? (
      <div className="qotd-pill is-compact qotd-pill-static-message">
        <span className="qotd-pill-text">
          {t(
            'outside_nyc.view_only_message',
            'This is how New York feels right now. Voting is only open inside the city.'
          )}
        </span>
      </div>
    ) : (
    qotdCardState === 'closed' ? (
      <button className="qotd-pill is-compact" onClick={openQotdCard}>
        <img src={qotdLogo} alt="QOTD" className="qotd-pill-logo" />
        <span className="qotd-pill-text">{t('qotd.pill_title', 'Question Of The Day')}</span>
        <span className={`qotd-pill-dot ${qotdHasVoted ? 'is-green' : 'is-red'}`} />
      </button>
    ) : (
      <div className="qotd-pill is-expanded">
        <div className="qotd-expanded-card">
          <div className="qotd-expanded-head">
            <img src={qotdLogo} alt="QOTD" className="qotd-expanded-logo" />
            <button className="qotd-close-btn" onClick={closeQotdCard} aria-label={t('qotd.close', 'Close')}>
              ×
            </button>
          </div>
          {qotd ? (
            <>
              <p className="qotd-question">{qotd.question_text}</p>
              {qotdCardState === 'prompt' ? (
                <div className="qotd-vote-row">
                  <button
                    className="qotd-vote-button qotd-vote-button-good"
                    onClick={() => submitQotdVote(true)}
                    disabled={qotdLoading || isSubmitting || !location}
                  >
                    {qotd.option_positive_text}
                  </button>
                  <button
                    className="qotd-vote-button qotd-vote-button-bad"
                    onClick={() => submitQotdVote(false)}
                    disabled={qotdLoading || isSubmitting || !location}
                  >
                    {qotd.option_negative_text}
                  </button>
                </div>
              ) : (
                <div className="qotd-results-fixed">
                  <div className="qotd-results-rows">
                    <div className="qotd-result-row">
                      <div className="qotd-result-label">
                        {qotd.option_positive_text} {Math.round(qotd.results.positive_percentage)}%
                      </div>
                      <div className="qotd-result-bar-track">
                        <div
                          className="qotd-result-bar-fill is-good"
                          style={{ width: `${qotd.results.positive_percentage}%` }}
                        />
                      </div>
                    </div>
                    <div className="qotd-result-row">
                      <div className="qotd-result-label">
                        {qotd.option_negative_text} {Math.round(qotd.results.negative_percentage)}%
                      </div>
                      <div className="qotd-result-bar-track">
                        <div
                          className="qotd-result-bar-fill is-bad"
                          style={{ width: `${qotd.results.negative_percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <p className="qotd-results-summary">
                    <span className="qotd-results-summary-votes">{qotd.results.total_votes.toLocaleString()}</span>{' '}
                    votes. You voted{' '}
                    <span
                      className={`qotd-results-summary-vote ${
                        qotd.results.user_vote?.is_positive ? 'is-good' : 'is-bad'
                      }`}
                    >
                      {(qotd.results.user_vote?.is_positive
                        ? qotd.option_positive_text
                        : qotd.option_negative_text
                      ).toUpperCase()}
                    </span>
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="qotd-loading">{qotdLoading ? t('status.loading', 'Loading...') : t('qotd.unavailable', 'QOTD unavailable')}</p>
          )}
          {isMobile ? renderAppStoreBadge('qotd-app-store-badge') : null}
        </div>
      </div>
    )
    )
  ) : null;

  const mobileContent = (
    <>
      {mobileHeader}

      {isLockedView ? (
        <div className={`locked-vote-panel ${unlockAnimationActive ? 'is-fading-out' : ''}`}>
          <h2 className="locked-vote-title">{mobileVotingCopy}</h2>
          {renderMobileVoteButtons('is-locked')}
        </div>
      ) : null}

      {!isLockedView && !isOutsideNYC && canVote && qotdCardState === 'closed' ? (
        <div className={`mood-revote-panel ${showMoodPrompt ? 'is-visible' : 'is-hidden'}`}>
          <h2 className="mood-revote-title">{mobileVotingCopy}</h2>
          {renderMobileVoteButtons()}
        </div>
      ) : null}

      {qotdPill}

      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="mobile-menu" onClick={(event) => event.stopPropagation()}>
            <button className="mobile-menu-close" onClick={() => setIsMobileMenuOpen(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <div className="mobile-menu-items">
              <a href={currentLanguage === 'en' ? '/about.html' : `/about-${currentLanguage}.html`} className="mobile-menu-item">{t('footer.about')}</a>
              <a href="https://shop.hwa.nyc/" className="mobile-menu-item" target="_blank" rel="noopener noreferrer">{t('footer.merch')}</a>
              <a href={currentLanguage === 'en' ? '/privacy.html' : `/privacy-${currentLanguage}.html`} className="mobile-menu-item">{t('footer.privacy')}</a>
              <a href={currentLanguage === 'en' ? '/terms.html' : `/terms-${currentLanguage}.html`} className="mobile-menu-item">{t('footer.terms')}</a>
              <a href={currentLanguage === 'en' ? '/support.html' : `/support-${currentLanguage}.html`} className="mobile-menu-item">{t('footer.support', 'Support')}</a>
              <a href="https://hwa.today" className="mobile-menu-item" target="_blank" rel="noopener noreferrer">hwa.today</a>
            </div>
            {renderAppStoreBadge('mobile-menu-app-store-badge')}
            <div className="mobile-menu-socials" aria-label="Social links">
              <a href="https://www.instagram.com/hwa.nyc/" className="mobile-menu-social-link" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <img src={instagramIcon} alt="" />
              </a>
              <a href="https://www.tiktok.com/@hwa...nyc" className="mobile-menu-social-link" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <img src={tiktokIcon} alt="" />
              </a>
              <a href="https://bsky.app/profile/hwa.nyc" className="mobile-menu-social-link" target="_blank" rel="noopener noreferrer" aria-label="Bluesky">
                <img src={blueskyIcon} alt="" />
              </a>
            </div>
          </div>
        </div>
      )}

      {isLanguageMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setIsLanguageMenuOpen(false)}>
          <div className="mobile-menu" onClick={(event) => event.stopPropagation()}>
            <button className="mobile-menu-close" onClick={() => setIsLanguageMenuOpen(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <div className="mobile-menu-items">
              {languageEntries.map(([code, name]) => (
                <button
                  key={code}
                  className={`mobile-menu-item ${code === currentLanguage ? 'active' : ''}`}
                  onClick={async () => {
                    if (code !== currentLanguage) {
                      await changeLanguage(code);
                      window.location.reload();
                    }
                    setIsLanguageMenuOpen(false);
                  }}
                >
                  {name}
                  {code === currentLanguage ? <span style={{ marginLeft: 'auto' }}>✓</span> : null}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );

  const panelContent = (
    <div className="floating-panel desktop-panel">
      <div className="liquid-glass-bg" />
      <div className="panel-content">
        <div className="panel-header">
          <div className="city-logo">{cityLogo ? <img src={cityLogo} alt="How We Are NYC" /> : null}</div>
          {boroughsData.length > 0 ? (
            <div className="boroughs-badges">
              {boroughsData.map(borough => {
                const hasVotes = borough.total_votes > 0;
                const positivePercentage = hasVotes ? (borough.positive_votes / borough.total_votes) * 100 : 50;
                const color = hasVotes ? getColorForPercentage(positivePercentage) : NO_VOTES_COLOR;
                const faceIcon = hasVotes ? getFaceIconByPercentage(positivePercentage) : getNoVotesFaceIcon();
                const displayName = BOROUGH_ABBREVIATIONS[borough.name] || borough.name;
                return (
                  <div key={borough.id} className="badge" style={{ backgroundColor: color }}>
                    <span>{displayName}</span>
                    {faceIcon ? <img src={faceIcon} alt="" /> : null}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="desktop-panel-main">
          {isLockedView ? (
            <div className={`desktop-mood-card ${unlockAnimationActive ? 'is-fading-out' : ''}`}>
              <p className="question-title">{lockedVotingCopy}</p>
              {renderMobileVoteButtons('is-locked')}
            </div>
          ) : null}

          {!isLockedView && !isOutsideNYC && canVote && qotdCardState === 'closed' ? (
            <div className="desktop-mood-card">
              <p className="question-title">{mobileVotingCopy}</p>
              {renderMobileVoteButtons()}
            </div>
          ) : null}

          {!isLockedView ? <div className="desktop-qotd-slot">{qotdPill}</div> : null}
        </div>
      </div>
    </div>
  );

  return isMobile ? mobileContent : panelContent;
};

export default FloatingPanel;

